// @ts-strict-ignore
import React, { useContext, useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import {
  Avatar,
  Box,
  Button,
  Callout,
  Combobox,
  Box as Form,
  Input,
  toast,
} from '@node-space/ui-components'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import { RequestError } from 'components/Errors/RequestError'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useCreateWalletMutation } from 'hooks/mutations'
import { useSupportedCurrenciesQuery } from 'hooks/queries/useSupportedCurrenciesQuery'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { NoWalletProfilesEmptyState } from 'pages/NestedCustomers/components/CustomerWallet/NoWalletProfilesEmptyState'
import { useFormOptions } from 'pages/NestedCustomers/hooks/useFormOptions'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { createWalletSchema } from 'pages/Wallets/schemas/createWalletSchema'
import { BaseErrorResponse } from 'types/beneficiaries'
import { WalletsCreateRequest } from 'types/wallets'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

export const WalletCreateForm = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [walletAction] = useContext(WalletActionContext)
  const { enableWalletCreationV2 } = useFeatureFlags()
  const { isPending, mutateAsync: createWallet } = useCreateWalletMutation()
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const { register, setValue, handleSubmit, formState, getValues, control } =
    useForm<WalletsCreateRequest>({
      defaultValues: {
        currency: '',
        description: '',
        profileId: '',
      },
      resolver: yupFormResolver<WalletsCreateRequest>(createWalletSchema(t)),
      mode: 'onChange',
      reValidateMode: 'onChange',
    })

  const [currency, profileId] = useWatch({
    control,
    name: ['currency', 'profileId'],
  })

  const {
    walletProfileOptions: walletProfileOptionsV2,
    isFetchingWalletProfiles: isFetchingWalletProfilesV2,
    profileMappings,
    availableCurrencies: availableCurrenciesV2,
  } = useFormOptions(currency, false)

  const { data: supportedCurrenciesV1, isFetching: isFetchingSupportedCurrenciesV1 } =
    useSupportedCurrenciesQuery(false)

  const walletProfileOptions = enableWalletCreationV2 ? walletProfileOptionsV2 : []
  const isFetchingWalletProfiles = enableWalletCreationV2
    ? isFetchingWalletProfilesV2
    : isFetchingSupportedCurrenciesV1

  const availableCurrencies = useMemo(() => {
    if (enableWalletCreationV2) {
      return availableCurrenciesV2
    }

    if (!supportedCurrenciesV1) return []

    return supportedCurrenciesV1
      .filter(currency => !currency.fiat)
      .map(currency => ({
        LeftElement: <Avatar name={currency.code?.toLowerCase()} size="small" />,
        leftText: currency.code,
        value: currency.code,
      }))
      .sort((a, b) => a.leftText.localeCompare(b.leftText))
  }, [enableWalletCreationV2, availableCurrenciesV2, supportedCurrenciesV1])

  const isCryptoCurrency = useMemo(() => {
    if (!currency) return true

    if (enableWalletCreationV2) {
      if (!profileMappings) return true
      if (profileMappings?.[currency]) {
        return profileMappings[currency].isCryptoProfile
      }
      return true
    } else {
      if (!supportedCurrenciesV1) return true
      const currencyData = supportedCurrenciesV1.find(c => c.code === currency)
      return currencyData ? !currencyData.fiat : true
    }
  }, [currency, enableWalletCreationV2, profileMappings, supportedCurrenciesV1])

  useEffect(() => {
    if (enableWalletCreationV2 && currency && walletProfileOptions?.length === 1) {
      setValue('profileId', walletProfileOptions?.[0]?.value)
    }
  }, [enableWalletCreationV2, currency, walletProfileOptions, setValue])

  const { onChange, ...restCurrencyProps } = register('currency')
  const { onChange: onProfileChange, ...restProfileProps } = register('profileId')

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.WALLET_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const onSubmit: SubmitHandler<WalletsCreateRequest> = async payload => {
    resetRequestError()
    try {
      await createWallet(payload)

      track.Amp.track(AmplitudeEvent.EOF_INITIATE_WALLET_SUCCESS, {
        category: AmplitudeEventCategory.WALLET,
        action: AmplitudeEventAction.CLICK,
        currency: getValues('currency'),
      })

      toast({
        header: t('walletCreated', {
          walletName: payload.description,
        }),
        variant: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['walletsV2'] })
      walletAction?.onClose()
    } catch (error) {
      const endpoint = enableWalletCreationV2 ? '/ledger/v2/wallets' : '/wallet'
      const sentryTraceId = logSentryErrorAndGetTraceId(
        `Failed API request to ${endpoint} with status ${(error as BaseErrorResponse)?.status} - useCreateWalletMutation`,
        error,
        {
          data: typeof payload === 'string' ? { message: payload } : payload,
        }
      )

      setRequestError({
        errorCode: (error as BaseErrorResponse).status,
        show: true,
        sentryTraceId,
      })
    }
  }

  if (!availableCurrencies?.length && !isFetchingWalletProfiles) {
    return <NoWalletProfilesEmptyState />
  }

  return (
    <Form
      flexCol
      width="full"
      tag="form"
      testId="wallet-create-form"
      gapY={16}
      onSubmit={handleSubmit(onSubmit)}
    >
      {requestError?.show && (
        <RequestError
          errorCode={requestError?.errorCode}
          sentryTraceId={requestError?.sentryTraceId}
        />
      )}

      <Combobox
        id="currency"
        label={t('selectCurrency')}
        testId="wallet-currency-select"
        name="currency"
        items={availableCurrencies}
        onChange={value => {
          setValue('currency', value)
          setValue('profileId', '')
        }}
        loading={isFetchingWalletProfiles}
        {...restCurrencyProps}
      />

      {!!currency && (
        <>
          {enableWalletCreationV2 && !isCryptoCurrency && (
            <Combobox
              key={currency}
              id="profileId"
              label={t('customers.selectPaymenMethodsToSupport')}
              searchPlaceholder={t('select')}
              items={walletProfileOptions}
              defaultValue={
                currency && walletProfileOptions?.length === 1
                  ? walletProfileOptions?.[0]?.value
                  : profileId
              }
              onChange={value => setValue('profileId', value)}
              disabled={!currency || (!!currency && walletProfileOptions?.length === 1)}
              loadingInline={isFetchingWalletProfiles}
              filter={false}
              {...restProfileProps}
            />
          )}

          <Input
            label={t('walletName')}
            inputMode="text"
            placeholder={t('enterName')}
            name="description"
            testId="wallet-name"
            error={!!formState.errors?.description}
            errorText={formState.errors?.description?.message}
            {...register('description')}
            onChange={e => {
              setValue('description', e?.target?.value, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }}
          />

          {enableWalletCreationV2 && !isCryptoCurrency && currency && (
            <Callout variant="info">{t('customers.walletCreationFormMessage')}</Callout>
          )}
        </>
      )}

      <Box flex justify="between" paddingT={16}>
        <Button testId="cancel-create-wallet" variant="secondary" onClick={walletAction?.onClose}>
          {t('cancel')}
        </Button>
        <Button
          testId="create-wallet"
          type="submit"
          variant="primary"
          loading={isPending}
          disabled={
            !formState.isValid || (enableWalletCreationV2 && !isCryptoCurrency && !profileId)
          }
        >
          {t('createWallet')}
        </Button>
      </Box>
    </Form>
  )
}
